// MarketPage — French. The console's language; the record's own content is French too, but the
// two are separate things. Shared words ('not collected', 'Loading…', 'Nothing matches', 'Clear
// the filter', 'Show {n} more', 'Tier', 'Status', 'Organisations', 'not scored') live in
// common.fr.ts or in another page's catalogue and are not repeated here.
//
// The RASTER's own vocabulary — blocks, cells, bands, sub-sectors, cantons — is NOT here. It
// lives beside its value in `src/shared/raster.v1.ts` and the page reads it through `labelFor`.
import { registerFr } from '../i18n/index.js';

registerFr({
  'Organisation raster · v1': 'Grille des organisations · v1',
  'Every organisation against one raster of comparable cells — who is a target, and why.':
    'Chaque organisation mesurée à une grille de cellules comparables — qui est une cible, et pourquoi.',
  'every organisation against one raster of comparable cells':
    'chaque organisation mesurée à une grille de cellules comparables',
  'profiled': 'profilées',

  // the rail
  'Profiled': 'Profilées',
  'organisations holding at least one profile cell':
    'organisations avec au moins une cellule de profil',
  'nobody has filled a single raster cell': 'personne n’a rempli une seule cellule de la grille',
  'Coverage': 'Couverture',
  'median share of the comparable cells that are filled':
    'part médiane des cellules comparables renseignées',
  'no organisation is profiled, so there is no median to take':
    'aucune organisation n’est profilée, il n’y a donc pas de médiane',
  'Targets': 'Cibles',
  '{w} watch · {p} park · {e} exclude': '{w} à suivre · {p} en attente · {e} exclues',
  'Sub-sectors': 'Sous-secteurs',
  'distinct sub-sectors on the record': 'sous-secteurs distincts au dossier',
  'no sub-sector cell has been filled': 'aucune cellule de sous-secteur n’a été renseignée',
  'Triggers 30d': 'Déclencheurs 30 j',
  'trigger cells standing true on a computation from the last 30 days':
    'cellules de déclencheur vraies dans un calcul des 30 derniers jours',
  'the market did not answer': 'le marché n’a pas répondu',
  'counting the population…': 'comptage de la population…',
  'nothing has answered yet': 'rien n’a encore répondu',

  // the filter bands
  'Name, legal name, ref…': 'Nom, raison sociale, référence…',
  'Search the population': 'Chercher dans la population',
  'All': 'Toutes',

  // the grid
  'pick two rows to compare their rasters': 'choisir deux lignes pour comparer leurs grilles',
  'one picked · pick a second to compare': 'une choisie · en choisir une seconde pour comparer',
  'The market did not answer': 'Le marché n’a pas répondu',
  'No organisation on the record matches these filters.':
    'Aucune organisation au dossier ne correspond à ces filtres.',
  'The record holds no active organisation. Organisations arrive from the seed import, from a directory monitor, or from a proposal you hold.':
    'Le dossier ne contient aucune organisation active. Les organisations arrivent par l’import initial, par un moniteur d’annuaire, ou par une proposition que vous retenez.',
  'No organisation holds a single raster cell yet, so every cell below reads "not collected". Open an organisation and use "Fill profile": it starts a research run against the raster, and every value it reads comes back as a proposal with its verbatim quote.':
    'Aucune organisation ne détient encore une seule cellule de la grille : tout ci-dessous indique « non relevé ». Ouvrez une organisation et utilisez « Remplir le profil » : cela lance une recherche contre la grille, et chaque valeur lue revient en proposition avec sa citation verbatim.',
  'The last refresh failed ({error}). The figures below are the previous answer.':
    'Le dernier rafraîchissement a échoué ({error}). Les chiffres ci-dessous sont la réponse précédente.',
  'Pick': 'Choisir',
  'Pick {name} for the comparison': 'Choisir {name} pour la comparaison',
  'Fit': 'Adéquation',
  'Access': 'Accès',
  'Composite': 'Produit',
  'Why': 'Pourquoi',
  'hide': 'masquer',
  '{shown} of {total}': '{shown} sur {total}',
  'This organisation has not been scored, so no rule has fired for it.':
    'Cette organisation n’a pas été évaluée : aucune règle ne s’est déclenchée.',
  'No rule fired. The composite is the product of the three floors — the honest score for an organisation nothing is known about.':
    'Aucune règle ne s’est déclenchée. Le produit est celui des trois planchers — la note honnête pour une organisation dont on ne sait rien.',
  'fit': 'adéquation',
  'access': 'accès',

  // the four tiers
  'target': 'cible',
  'watch': 'à suivre',
  'park': 'en attente',
  'exclude': 'exclue',

  // the comparison
  'Compare': 'Comparaison',
  'the same raster, cell by cell — a row empty on both sides is a fact about our coverage':
    'la même grille, cellule par cellule — une ligne vide des deux côtés dit quelque chose de notre couverture',

  // the population
  'Population': 'Population',
  'the federation lists and cantonal registers the names came from':
    'les listes de membres des faîtières et les registres cantonaux d’où viennent les noms',
  'The directories could not be read: {error}':
    'Les annuaires n’ont pas pu être lus : {error}',
  'No directory is registered yet. A directory is a federation member list — AVOP, Fachverband Sucht, the DGCS list of établissements socio-éducatifs — whose unknown names arrive as organisation proposals with the list line as their verbatim quote.':
    'Aucun annuaire n’est encore enregistré. Un annuaire est une liste de membres d’une faîtière — AVOP, Fachverband Sucht, la liste DGCS des établissements socio-éducatifs — dont les noms inconnus arrivent en propositions d’organisation, la ligne de la liste servant de citation verbatim.',
  'Directory': 'Annuaire',
  'Federation': 'Faîtière',
  'Members seen': 'Membres relevés',
  'Waiting as proposals': 'En attente comme propositions',
  'Last import': 'Dernier import',
  'never imported': 'jamais importé',

  // ── les unités et les valeurs héritées du parent ──
  'Top-level only': 'Niveau supérieur seulement',
  'A unit is a department, a site or a programme under another organisation. With this on, the raster compares organisations only.':
    'Une unité est un département, un site ou un programme rattaché à une autre organisation. Avec ce filtre, la grille ne compare que des organisations.',
  '{n} units hidden by “Top-level only”': '{n} unités masquées par « Niveau supérieur seulement »',
  '{n} units hidden among the rows loaded': '{n} unités masquées parmi les lignes chargées',
  'counted among the rows loaded; the units in the rest of the set were not counted':
    'compté parmi les lignes chargées ; les unités du reste de l’ensemble n’ont pas été comptées',
  'Every organisation these filters match is a unit of another one, and “Top-level only” is on.':
    'Toutes les organisations retenues par ces filtres sont des unités d’une autre, et « Niveau supérieur seulement » est actif.',
  'unit of': 'unité de',
  'another organisation': 'une autre organisation',
  'from parent': 'du parent',
  'held by {name}, this unit’s parent — not a fact about the unit':
    'retenu par {name}, le parent de cette unité — ce n’est pas un fait sur l’unité',
  'held by this unit’s parent — not a fact about the unit':
    'retenu par le parent de cette unité — ce n’est pas un fait sur l’unité',
  'Review': 'Revue',
}, 'MarketPage');
